import { useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { cleanQuery, displayTag } from '@engineer-os/chat';
import { clockTime, dayLabelWithYear } from '../../lib/format';
import { useStrings } from '../../state/useLocale';
import { Button, Machine, Parts } from '../../ui';
import type { ChatRow } from '../../state/useChat';
import { RichText } from './CustomEmoji';
import { Identity } from './Identity';

/**
 * Finding something that was said, and what comes back.
 *
 * Two components in one file because they are one feature: the field is in the
 * header and the results are where the conversation was, and splitting them
 * across two files would put half a feature's strings and half its decisions
 * somewhere the other half cannot see. Nothing else imports either of them.
 *
 * A room keeps a thousand messages and a panel shows a dozen, so search is the
 * other half of the answer that pinning gives — pins are what somebody decided
 * to keep, and this is everything nobody thought to. It is a `LIKE` over one
 * room, which is exactly what it says it is: there is no cross-room search,
 * because a second room is a second Durable Object and asking ninety-nine of
 * them a question is a fan-out this product has no reason to pay for.
 *
 * The query is validated by the protocol's own `cleanQuery` before anything is
 * sent, and the sentence on the disabled button is the protocol's own
 * `searchRule` in this language. A panel that let somebody press Search on one
 * character and then explained the refusal afterwards would be a panel that
 * knew the rule and kept it to itself.
 *
 * Results replace the conversation rather than floating over it. In 280px
 * there is no room for both, and a result list that covered half the messages
 * would be a list you cannot read the answer in. Leaving is one button, and
 * clicking any result leaves too — it goes to the message in the room, with
 * the conversation around it, which is where the answer actually is.
 */
export interface SearchBarProps {
  open: boolean;
  onOpen(): void;
  /** Shut the field and throw away whatever it found. */
  onClose(): void;
  onSearch(q: string): void;
  busy: boolean;
}

export function SearchBar({ open, onOpen, onClose, onSearch, busy }: SearchBarProps) {
  const s = useStrings().chat;
  const [value, setValue] = useState('');
  const q = cleanQuery(value);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (q) onSearch(q);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    // Ours, not the panel's: one Escape leaves the search, a second closes
    // the chat. Somebody who searched and changed their mind should not have
    // to reopen the conversation they were in.
    e.preventDefault();
    e.stopPropagation();
    setValue('');
    onClose();
  };

  if (!open) {
    return (
      <button type="button" className="chat-search-btn" aria-label={s.search.open} title={s.search.open} onClick={onOpen}>
        <svg viewBox="0 0 12 12" aria-hidden="true" width="11" height="11">
          <circle cx="5" cy="5" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M7.6 7.6L10.5 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
    );
  }

  return (
    <form className="chat-search" onSubmit={submit} onKeyDown={onKeyDown}>
      <input
        className="chat-input chat-search-field"
        type="search"
        value={value}
        placeholder={s.search.placeholder}
        aria-label={s.search.open}
        autoComplete="off"
        spellCheck={false}
        autoFocus
        // Either script may be searched for, so the field takes its direction
        // from the query rather than from the page.
        dir="auto"
        onChange={(e) => setValue(e.target.value)}
      />
      <Button type="submit" variant="quiet" disabled={!q || busy} title={q ? s.search.go : s.search.rule()}>
        {busy ? s.search.busy : s.search.go}
      </Button>
      <button
        type="button"
        className="chat-x"
        aria-label={s.search.close}
        title={s.search.close}
        onClick={() => {
          setValue('');
          onClose();
        }}
      >
        <svg viewBox="0 0 12 12" aria-hidden="true" width="10" height="10">
          <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>
    </form>
  );
}

export interface SearchResultsProps {
  q: string;
  results: ChatRow[];
  loading: boolean;
  /** Go to one, in the room that was searched. */
  onOpen(id: number): void;
  onBack(): void;
}

/**
 * What came back, newest first, in the conversation's place.
 *
 * Deliberately not the message list. The list's whole job is scroll
 * anchoring — follow the bottom, hold your place when a page arrives at the
 * top — and every one of those behaviours is wrong for a set of results that
 * does not grow and has no bottom to follow. So this is a plain list of rows,
 * each one a button, and the moment you press one you are back in the room.
 */
export function SearchResults({ q, results, loading, onOpen, onBack }: SearchResultsProps) {
  const s = useStrings().chat;
  const query = <span dir="auto">{q}</span>;

  return (
    <div className="chat-found scroll" role="region" aria-label={s.search.open}>
      <div className="chat-found-head">
        <p className="label">
          {loading ? s.search.busy : <Parts of={results.length > 0 ? s.search.found(results.length, query) : s.search.none(query)} />}
        </p>
        <button type="button" className="chat-link" onClick={onBack}>
          {s.search.back}
        </button>
      </div>

      {results.map((row) => {
        const when = new Date(row.ts);
        return (
          <button key={row.id} type="button" className="chat-found-row" title={s.search.result} onClick={() => onOpen(row.id)}>
            <span className="msg-head">
              <Identity m={row} />
              {/* The tag and the clock are the machine's and read left to
                  right in both languages (docs/i18n.md §7). The date is on
                  the title rather than in the row: a result may be from any
                  day, and a date beside every line would be wider than the
                  line. */}
              <Machine className="msg-tag">#{displayTag(row.tag)}</Machine>
              <time className="msg-time" dir="ltr" dateTime={when.toISOString()} title={dayLabelWithYear(when)}>
                {clockTime(when)}
              </time>
            </span>
            <span className="msg-text" dir="auto">
              <RichText text={row.text} />
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default SearchBar;
